import React from 'react';
import NProgress from 'nprogress';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { z, ZodError } from 'zod';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { firestore, loadAnalytics } from '../../../lib';
import {
  Application,
  Education,
  educationSchema,
  Qualification,
  qualificationsSchema,
} from '../../../models';
import { Button } from '../../commons';
import { Input, RadioCheckInput } from '../../inputs';
import QualificationsForm from './QualificationsForm';

interface EducationFormProps {
  education: Education;
  application: Application;
  isReadOnly?: boolean;
}

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

export default function EducationForm({
  application,
  education,
  isReadOnly = false,
}: EducationFormProps) {
  const router = useRouter();
  const [qualifications, setQualifications] = React.useState<Qualification[]>(
    education?.qualifications ?? []
  );

  React.useEffect(
    function () {
      if (Array.isArray(education?.qualifications)) {
        setQualifications(education?.qualifications ?? []);
      }
    },
    [education?.qualifications]
  );

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    let data: z.infer<typeof educationSchema> = null;
    try {
      data = educationSchema.parse({
        ...(education ?? {}),
        ...Object.fromEntries(formData.entries()),
        qualifications,
      });
    } catch (err) {
      console.log(err);
      if (err instanceof ZodError) {
        return toastError(err.issues[0].message);
      }
      return toastError(
        'An error has occurred, please try again later. CODE: E41'
      );
    }
    try {
      NProgress.start();
      await updateDoc(doc(firestore, 'applications', application?.id), {
        lastUpdated: Timestamp.fromDate(new Date()),
        step: '4',
        education: {
          ...data,
        },
      });
      logEvent(loadAnalytics(), 'step_completed', {
        step: '4',
        finishedTime: new Date().toISOString(),
      });
      try {
        posthog?.capture(
          'step_completed',
          {
            step: '4',
            finishedTime: new Date().toISOString(),
          },
          { transport: 'sendBeacon' }
        );
      } catch (err) {
        posthog.captureException(err);
      }
      await router.push(
        '/request-scholarship/[...id]',
        '/request-scholarship/5'
      );
    } catch (err) {
      NProgress.done();
      posthog?.captureException(err);
      Sentry.addBreadcrumb({ level: 'error', category: 'step-4' });
      Sentry.captureException(err);
      toastError('An error has occurred, please try again later. CODE: E42');
    }
  };

  return (
    <form onSubmit={onSubmit} method="POST">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Input
          type="text"
          label="High School Attended"
          name="highSchool"
          defaultValue={education?.highSchool ?? ''}
          placeholder="High School Attended"
          readOnly={isReadOnly}
          disabled={isReadOnly}
        />
        <Input
          type="text"
          label="Year of High School Graduation"
          name="year"
          defaultValue={education?.year ?? ''}
          placeholder="Year of High School Graduation"
          readOnly={isReadOnly}
          disabled={isReadOnly}
        />
      </div>
      <div className="mt-8 flex flex-col">
        <p className="text-gray-500 text-[14px] font-bold mb-2">
          Are you a first-generation college student?
        </p>
        <RadioCheckInput
          type="radio"
          name="firstGeneration"
          label="Yes, I am the first among my parents and grandparents to attend college"
          labelClassName="font-bold text-[20px] text-neutral-500"
          className="mb-2"
          value="on"
          defaultChecked={education?.firstGeneration === 'on'}
          readOnly={isReadOnly}
        />
        <RadioCheckInput
          type="radio"
          name="firstGeneration"
          label="No, either my parent(s) or grandparent(s) have attended college"
          labelClassName="font-bold text-[20px] text-neutral-500"
          value="off"
          defaultChecked={education?.firstGeneration === 'off'}
          disabled={isReadOnly}
        />
      </div>
      <div className="mt-8">
        <QualificationsForm
          qualifications={qualifications}
          setQualifications={setQualifications}
          isReadOnly={isReadOnly}
        />
      </div>
      {isReadOnly ? null : (
        <div className="mt-8 flex">
          <Button type="submit" color="blue" className="ml-auto w-[135px]">
            Continue
          </Button>
        </div>
      )}
    </form>
  );
}
